import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SettingsIcon from "@mui/icons-material/Settings";
import NotesIcon from "@mui/icons-material/EventNote";
import RefreshIcon from "@mui/icons-material/Refresh";
import { ToastAlert } from "../../components/ToastAlert";
import { Loading } from "../../components/Loading/Loading";
import Header from "../../components/Header";
import IconsBar from "../../components/IconsBar/IconsBar";

import { AuthContext } from "../../contexts/Auth/AuthContext";
import { getErrorMessage } from "../../utils/errorHandler";

import {
  listStages,
  listSegments,
  listSources,
  listLeads,
  updateLeadStage,
  createLead,
  searchLeads,
} from "../../api/endpoints/enrollmentCrm";

import NewLeadDialog from "./NewLeadDialog";
import LeadDetailsDialog from "./LeadDetailsDialog";
import StagesSettingsDialog from "./StagesSettingsDialog";
import SegmentsSettingsDialog from "./SegmentsSettingsDialog";
import SourcesSettingsDialog from "./SourcesSettingsDialog";

const PageContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #1e958c 0%, #0b4f6c 40%, #f5f7fb 100%);
`;

/** Cartão principal — mesmo padrão de GradeLogs / Grades / Stats */
const MainCard = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 16px auto 24px;
  padding: 20px 16px 28px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.16);
  display: flex;
  flex-direction: column;
  gap: 18px;
  backdrop-filter: blur(8px);
  overflow-x: hidden;
  flex: 1;
`;

const TitleToolbar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  width: 100%;

  @media (min-width: 720px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const PageTitle = styled.span`
  font-weight: 700;
  font-size: 22px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #1e958c;
  margin: 0;
  text-align: center;

  @media (min-width: 720px) {
    text-align: left;
  }
`;

const ToolbarActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;

  @media (min-width: 720px) {
    justify-content: flex-end;
  }
`;

const SearchPanel = styled(Paper)`
  && {
    padding: 16px;
    border-radius: 12px;
    background: #f5f7fb;
    box-shadow: none;
    border: 1px solid rgba(15, 23, 42, 0.06);
  }
`;

const BoardWrap = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px;
  padding: 8px 0 0;
  min-height: 0;
  overflow-x: hidden;
`;

const Column = styled.div`
  background-color: #d2dae5;
  border: 1px solid rgba(15, 23, 42, 0.09);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  min-height: 220px;
`;

const ColumnHeader = styled.div`
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ColumnHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ColumnBody = styled.div`
  padding: 10px;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: calc(100vh - 300px);
`;

const LeadCard = styled.div`
  background-color: #fff;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.15s ease-in-out;
  cursor: pointer;
  margin-bottom: 12px;
  user-select: none;

  &:hover {
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23);
  }
`;

const LeadCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 8px;
`;

const DragHint = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
  margin-left: 8px;
`;

const LeadIdBadge = styled.span`
  display: inline-block;
  background-color: #f3f3f3;
  color: rgba(0, 0, 0, 0.55);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
`;

const defaultSearchFields = [
  "id",
  "name",
  "phone",
  "email",
  "studentName",
  "studentAge",
  "schoolYear",
  "segment",
  "grade",
  "shift",
  "previousSchool",
  "motherName",
  "motherPhone",
  "fatherName",
  "fatherPhone",
  "interestDetails",
  "source",
  "referredBy",
  "nextFollowUpDate",
  "lastContactDate",
  "stageName",
  "segmentName",
  "sourceName",
  "responsibleUserName",
];

const EnrollmentCRM = () => {
  const { user } = useContext(AuthContext);

  const isAdmin = useMemo(() => {
    const lvl = user?.level?.toLowerCase?.().trim();
    return lvl === "admin";
  }, [user?.level]);

  const [loading, setLoading] = useState(true);

  const [stages, setStages] = useState([]);
  const [segments, setSegments] = useState([]);
  const [sources, setSources] = useState([]);
  const [leads, setLeads] = useState([]);

  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchStageIds, setSearchStageIds] = useState([]);

  const [stageSettingsOpen, setStageSettingsOpen] = useState(false);
  const [segmentSettingsOpen, setSegmentSettingsOpen] = useState(false);
  const [sourceSettingsOpen, setSourceSettingsOpen] = useState(false);

  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const [leadDetailsOpen, setLeadDetailsOpen] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [initialStageId, setInitialStageId] = useState(null);

  const [toastShow, setToastShow] = useState(false);
  const [toastType, setToastType] = useState("success");
  const [toastMessage, setToastMessage] = useState("");

  const showToast = useCallback((type, message) => {
    setToastType(type);
    setToastMessage(message);
    setToastShow(true);
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [stagesRes, segmentsRes, sourcesRes, leadsRes] = await Promise.all([
        listStages(),
        listSegments(),
        listSources(),
        listLeads(),
      ]);

      setStages(stagesRes || []);
      setSegments(segmentsRes || []);
      setSources(sourcesRes || []);
      setLeads(leadsRes || []);
    } catch (err) {
      showToast("error", getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const sortedStages = useMemo(() => {
    return [...stages].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [stages]);

  const leadsByStage = useCallback(
    (stageId) => {
      return (leads || []).filter((l) => Number(l.stageId) === Number(stageId));
    },
    [leads]
  );

  const handleDragStart = (e, leadId) => {
    e.dataTransfer.setData("text/plain", String(leadId));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDrop = async (e, destinationStageId) => {
    e.preventDefault();
    const leadIdRaw = e.dataTransfer.getData("text/plain");
    const leadId = parseInt(leadIdRaw, 10);
    if (!leadId || Number.isNaN(leadId)) return;
    const destinationIdNum = Number(destinationStageId);

    const lead = leads.find((l) => Number(l.id) === leadId);
    if (!lead) return;
    if (Number(lead.stageId) === destinationIdNum) return;

    // Feedback imediato
    setLeads((prev) => prev.map((l) => (Number(l.id) === leadId ? { ...l, stageId: destinationIdNum } : l)));

    try {
      await updateLeadStage(leadId, destinationIdNum);
    } catch (err) {
      showToast("error", getErrorMessage(err));
      fetchAll();
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleOpenNewLead = (stageId = null) => {
    const firstStageId = sortedStages.length > 0 ? sortedStages[0].id : null;
    setInitialStageId(stageId ?? firstStageId);
    setNewLeadOpen(true);
  };

  const handleSaveNewLead = async (payload) => {
    await createLead(payload);
    setNewLeadOpen(false);
    await fetchAll();
  };

  const handleOpenLeadDetails = (leadId, tabIndex = 0) => {
    setSelectedLeadId(leadId);
    setSelectedTabIndex(tabIndex);
    setLeadDetailsOpen(true);
  };

  const handleLeadDeleted = async () => {
    setLeadDetailsOpen(false);
    setSelectedLeadId(null);
    await fetchAll();
  };

  const handleLeadSaved = async () => {
    setLeadDetailsOpen(false);
    setSelectedLeadId(null);
    await fetchAll();
  };

  const handleSearch = async () => {
    const term = searchTerm.trim();
    const stagesForSearch = Array.isArray(searchStageIds) ? searchStageIds : [];
    const hasTerm = term.length > 0;
    const hasStages = stagesForSearch.length > 0;

    if (!hasTerm && !hasStages) {
      setIsSearchMode(false);
      await fetchAll();
      return;
    }

    setLoading(true);
    try {
      const result = await searchLeads({
        searchTerm: hasTerm ? term : "",
        fields: defaultSearchFields,
        stages: stagesForSearch,
        exactMatch: false,
      });

      setLeads(result?.leads || []);
      setIsSearchMode(true);
    } catch (err) {
      showToast("error", getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = async () => {
    setSearchTerm("");
    setSearchStageIds([]);
    setIsSearchMode(false);
    await fetchAll();
  };

  return (
    <PageContainer>
      <Header />
      <IconsBar />
      {loading ? <Loading /> : null}
      <MainCard>
        <TitleToolbar>
          <PageTitle>CRM de Matrículas</PageTitle>
          <ToolbarActions>
            <Button
              variant="contained"
              onClick={() => handleOpenNewLead()}
              startIcon={<AddIcon />}
              disabled={loading}
            >
              Novo Lead
            </Button>

            {isAdmin ? (
              <>
                <IconButton onClick={() => setStageSettingsOpen(true)} disabled={loading} size="medium" aria-label="Configurar estágios">
                  <SettingsIcon />
                </IconButton>
                <IconButton onClick={() => setSegmentSettingsOpen(true)} disabled={loading} size="medium" aria-label="Segmentos">
                  <Chip size="small" label="Seg" />
                </IconButton>
                <IconButton onClick={() => setSourceSettingsOpen(true)} disabled={loading} size="medium" aria-label="Fontes">
                  <Chip size="small" label="Fonte" />
                </IconButton>
              </>
            ) : null}
          </ToolbarActions>
        </TitleToolbar>

        <SearchPanel elevation={0}>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <TextField
              label="Buscar"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
              size="small"
              sx={{ minWidth: 280, flex: 1 }}
            />

            <Select
              multiple
              value={searchStageIds}
              onChange={(e) => setSearchStageIds(e.target.value)}
              size="small"
              renderValue={(selected) => {
                if (!selected?.length) return "Filtrar por estágios";
                const ids = selected.map((s) => Number(s));
                const names = sortedStages
                  .filter((s) => ids.includes(Number(s.id)))
                  .map((s) => s.name);
                return names.join(", ");
              }}
              sx={{ minWidth: 320, flex: 1 }}
            >
              {sortedStages.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>

            <Button variant="contained" onClick={handleSearch} startIcon={<RefreshIcon />} disabled={loading}>
              Buscar
            </Button>

            {isSearchMode ? (
              <Button variant="outlined" onClick={handleClearSearch} disabled={loading}>
                Limpar busca
              </Button>
            ) : null}
          </Box>

          <DragHint>Arraste um lead para trocar de estágio.</DragHint>
        </SearchPanel>

        <Divider sx={{ borderColor: "rgba(15, 23, 42, 0.08)" }} />

        <BoardWrap>
          {sortedStages.map((stage) => {
            const stageLeads = leadsByStage(stage.id);

            return (
              <Column
                key={stage.id}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.id)}
              >
                <ColumnHeader>
                  <ColumnHeaderLeft>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: 1,
                        bgcolor: stage.color || "#3498db",
                        border: "1px solid rgba(0,0,0,0.08)",
                      }}
                    />
                    <Typography sx={{ fontWeight: 900 }}>{stage.name}</Typography>
                    <Chip size="small" label={stageLeads.length} sx={{ ml: 1 }} />
                  </ColumnHeaderLeft>
                  <IconButton onClick={() => handleOpenNewLead(stage.id)} size="small" disabled={loading}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </ColumnHeader>

                <ColumnBody>
                  {stageLeads.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 2 }}>
                      Nenhum lead.
                    </Typography>
                  ) : (
                    stageLeads.map((lead) => (
                      <LeadCard
                        key={lead.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, lead.id)}
                        onClick={() => handleOpenLeadDetails(lead.id, 0)}
                      >
                        <LeadCardHeader>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography sx={{ fontWeight: 900 }}>{lead.name}</Typography>
                            <LeadIdBadge>ID: {lead.id}</LeadIdBadge>
                          </Box>
                        </LeadCardHeader>

                        <Typography variant="body2" color="text.secondary">
                          Aluno: {lead.studentName || "-"}
                        </Typography>

                        {lead.schoolYear && lead.grade ? (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {lead.schoolYear} - {lead.grade}
                          </Typography>
                        ) : null}

                        {lead.responsibleUser?.name ? (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Responsável: {lead.responsibleUser.name}
                          </Typography>
                        ) : null}

                        <Box display="flex" justifyContent="flex-end" alignItems="center" mt={1}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenLeadDetails(lead.id, 1);
                            }}
                          >
                            <NotesIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </LeadCard>
                    ))
                  )}
                </ColumnBody>
              </Column>
            );
          })}
        </BoardWrap>
      </MainCard>

      <ToastAlert
        show={toastShow}
        setShow={setToastShow}
        type={toastType}
        message={toastMessage}
      />

      <NewLeadDialog
        open={newLeadOpen}
        onClose={() => setNewLeadOpen(false)}
        onSave={handleSaveNewLead}
        stages={sortedStages}
        segments={segments}
        sources={sources}
        initialStageId={initialStageId}
        currentUser={user}
      />

      <LeadDetailsDialog
        open={leadDetailsOpen}
        leadId={selectedLeadId}
        onClose={() => setLeadDetailsOpen(false)}
        stages={sortedStages}
        segments={segments}
        sources={sources}
        currentUser={user}
        isAdmin={isAdmin}
        onDeleted={handleLeadDeleted}
        onSaved={handleLeadSaved}
        initialTabIndex={selectedTabIndex}
      />

      <StagesSettingsDialog
        open={stageSettingsOpen}
        onClose={() => setStageSettingsOpen(false)}
        stages={stages}
        onSaved={fetchAll}
        isAdmin={isAdmin}
      />

      <SegmentsSettingsDialog
        open={segmentSettingsOpen}
        onClose={() => setSegmentSettingsOpen(false)}
        segments={segments}
        onSaved={fetchAll}
        isAdmin={isAdmin}
      />

      <SourcesSettingsDialog
        open={sourceSettingsOpen}
        onClose={() => setSourceSettingsOpen(false)}
        sources={sources}
        onSaved={fetchAll}
        isAdmin={isAdmin}
      />
    </PageContainer>
  );
};

export default EnrollmentCRM;

